/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form');
      this.optionSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom)');
      this.nativeSelector = document.getElementById(`variants-${this.section.dataset.section}`);
      this.data = this.getVariantData();
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
      this.selectedSizeOption();

      this.updateAvailability();
    
      this.updateAddToCartButton();
      this.addEventListener('change', this.handleVariantChange.bind(this));

      this.setAttribute('loaded', '');
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.updateUrl(evt);
      this.updateVariantInput();
      this.updateAddToCartButton();
      this.updateAvailability();
      this.variantOptionHide(evt);
      this.updatePrice();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateBarcode();
      VariantPicker.updateLabelText(evt);

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.productForm,
          variant: this.variant,
          allVariants: this.data.variants,
          selectedOptions: this.selectedOptions
        }
      }));
    }

    /**
     * Updates the "Add to Cart" button label and status.
     */
    updateAddToCartButton() {
      if (!this.productForm) return;
      if (this.selectedOptions.indexOf(null) > -1) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;
      const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;

      this.addBtn.disabled = !variantAvailable;
      this.addBtn.textContent = variantAvailable
        ? this.addBtn.dataset.addToCartText
        : unavailableStr;
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMode } = this.dataset; // 'down' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMode === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default') ? '' : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the colour option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability = this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-price .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.variants[0];
        for (let i = 1; i < this.data.variants.length; i += 1) {
          if (this.data.variants[i].price < variant.price) variant = this.data.variants[i];
        }
      }

      if (variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        // Update current price and original price if on sale.
        priceCurrentEl.innerHTML = this.data.formatted[variant.id].price;
        if (priceWasEl) priceWasEl.innerHTML = this.data.formatted[variant.id].compareAtPrice || '';

        // Update unit price, if specified.
        if (variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = variant.unit_price_measurement.reference_value;
          const unit = variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
          unitPriceEl.hidden = false;
        } else if (unitPriceEl) {
          unitPriceEl.hidden = true;
        }

        this.price.classList.toggle('price--on-sale', variant.compare_at_price > variant.price);
        this.price.classList.toggle('price--sold-out', !variant.available && !this.preSelection);
      }

      this.price.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
      this.price.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcode = this.barcode || this.section.querySelector('.product-barcode__value');
      if (!this.barcode) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
      this.barcode.parentNode.hidden = !barcodeAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {      
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      const url = this.variant ? `${this.dataset.url}?variant=${this.variant.id}` : this.dataset.url;
      window.history.replaceState({ }, '', url);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms = this.forms || this.section.querySelectorAll('.js-product-form, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('[name="id"]');
        input.value = this.variant ? this.variant.id : '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }


     /**
     * On variant first option change hide second option
     */


   selectedSizeOption(){
      if(typeof this.variant != 'undefined'){
      let preSelected = this.variant.option1.replace(/\s+/g, '-').toLowerCase();
      if( document.querySelectorAll(".select-flavor .new_col").length > 0 ){
        const variantShowHide = document.querySelectorAll(".select-flavor .new_col");
        
        if( preSelected == "4.5-oz-decadent-flavors" ){
          var i=1;
          let optionSizes = window.theme.sizeOptionName.option4Oz;
          
          if( optionSizes !="" ){
            let optionSize = optionSizes.split(",");
            if( optionSize.length > 0 ){
              $(".select-flavor .new_col").addClass("hide_variant");
              $(".select-flavor .new_col").removeClass("show_variant");
              $('.variant-label').hide();
              $('.select-flavor .variant-label.4-oz').show();
              $('.giftbox-msg').hide();
              $('.select-flavor .giftbox-msg.4-oz').show().text('( 4.5 Oz Gift Pack Comes With 8 Cookies )');
              optionSize.forEach(function(opn, indx){
                variantShowHide.forEach(function(item, inx){
                  if( item.classList.contains($.trim(opn)) ){
                    item.classList.remove("hide_variant");
                    item.classList.add("show_variant");
                    if( item.classList.contains("show_variant") && i == 1 ){
                      if( typeof evt !=="undefined"){
                        if( evt.target.closest(".select-size") != null ){
                          $(item).find('input[type="radio"]').trigger("click");
                        }
                      }i++;
                    }
                  }else{
                    //item.classList.add("hide_variant");
                    //item.classList.remove("show_variant");
                  }
                });
              })
            }else{ //$(".new_col").add("hide_variant");
            }
          }
        }else if( preSelected == "1-pack" ){
          var i=1;
          let optionSizes = window.metadata.option_for_pack1;
          
           if( optionSizes !="" ){
            let optionSize = optionSizes.split(",");
            if( optionSize.length > 0 ){
              $(".select-flavor .new_col").addClass("hide_variant");
              $(".select-flavor .new_col").removeClass("show_variant");
              $('.variant-label').hide();
              $('.giftbox-msg').hide();
              // $('.select-flavor .variant-label.4-oz').show();
              optionSize.forEach(function(opn, indx){
                variantShowHide.forEach(function(item, inx){
                  if( item.classList.contains($.trim(opn)) ){
                    item.classList.remove("hide_variant");
                    item.classList.add("show_variant");
                    if( item.classList.contains("show_variant") && i == 1 ){
                      if( typeof evt !=="undefined"){
                        if( evt.target.closest(".select-size") != null ){
                          $(item).find('input[type="radio"]').trigger("click");
                        }
                      }i++;
                    }
                  }else{
                    //item.classList.add("hide_variant");
                    //item.classList.remove("show_variant");
                  }
                });
              })
            }else{ //$(".new_col").add("hide_variant");
            }
          }
        }else if( preSelected == "4-pack" ){
          var i=1;
          let optionSizes = window.metadata.option_for_pack2;
          
           if( optionSizes !="" ){
            let optionSize = optionSizes.split(",");
            if( optionSize.length > 0 ){
              $(".select-flavor .new_col").addClass("hide_variant");
              $(".select-flavor .new_col").removeClass("show_variant");
              $('.variant-label').hide();
              $('.giftbox-msg').hide();
              // $('.select-flavor .variant-label.4-oz').show();
              optionSize.forEach(function(opn, indx){
                variantShowHide.forEach(function(item, inx){
                  if( item.classList.contains($.trim(opn)) ){
                    item.classList.remove("hide_variant");
                    item.classList.add("show_variant");
                    if( item.classList.contains("show_variant") && i == 1 ){
                      if( typeof evt !=="undefined"){
                        if( evt.target.closest(".select-size") != null ){
                          $(item).find('input[type="radio"]').trigger("click");
                        }
                      }i++;
                    }
                  }else{
                    //item.classList.add("hide_variant");
                    //item.classList.remove("show_variant");
                  }
                });
              })
            }else{ //$(".new_col").add("hide_variant");
            }
          }
        }else if( preSelected =="3-oz" ){
          var j=1;
          let optionSizes = window.theme.sizeOptionName.option1Oz;
            
          if( optionSizes !="" ){
            let optionSize = optionSizes.split(",");
            if( optionSize.length > 0 ){
              $(".select-flavor .new_col").addClass("hide_variant");
              $(".select-flavor .new_col").removeClass("show_variant");
              $('.variant-label').hide();
              $('.select-flavor .variant-label.1_3-oz').show().text('3 oz Flavors');
              $('.giftbox-msg').hide();
              $('.select-flavor .giftbox-msg.1_3oz').show().text('( 3 Oz Gift Pack Comes With 12 Cookies )');
              optionSize.forEach(function(opn, indx){
                variantShowHide.forEach(function(item, inx){
                  if( item.classList.contains($.trim(opn)) ){
                    item.classList.remove("hide_variant");
                    item.classList.add("show_variant");
                    
                    if( item.classList.contains("show_variant") && j == 1 ){
                      if( typeof evt !=="undefined"){
                        if( evt.target.closest(".select-size") != null ){
                          $(item).find('input[type="radio"]').trigger("click");
                        }
                      }j++;
                    }
                  }else{
                    //item.classList.add("hide_variant");
                    //item.classList.remove("show_variant");
                  }
                });
              })
            }else{ //$(".new_col").add("hide_variant");
            }
          }
        }else if( preSelected =="1.5-oz" ){
        var k = 1;
        let optionSizes1 = window.theme.sizeOptionName.option1Oz;
        let optionSizes2 = window.theme.sizeOptionName.gluten_free;
        
        // Merge both optionSizes arrays into one
        let combinedOptionSizes = optionSizes1.split(",").concat(optionSizes2.split(","));
        
        if (combinedOptionSizes.length > 0) {
          $(".select-flavor .new_col").addClass("hide_variant");
          $(".select-flavor .new_col").removeClass("show_variant");
          $('.variant-label').hide();
          $('.select-flavor .variant-label.gluten-free').show();
          $('.select-flavor .variant-label.1_3-oz').show().text('1.5 oz Flavors');
          $('.giftbox-msg').hide();
          $('.select-flavor .giftbox-msg.1_3oz').show().text('( 1.5 Oz Gift Pack Comes With 12 Cookies )');
          combinedOptionSizes.forEach(function(opn) {
            variantShowHide.forEach(function(item) {
              if (item.classList.contains($.trim(opn))) {
                item.classList.remove("hide_variant");
                item.classList.add("show_variant");
        
                if (item.classList.contains("show_variant") && k == 1) {
                  if (typeof evt !== "undefined") {
                    if (evt.target.closest(".select-size") != null) {
                      $(item).find('input[type="radio"]').trigger("click");
                    }
                  }
                  k++;
                }
              } else {
                // item.classList.add("hide_variant");
                // item.classList.remove("show_variant");
              }
            });
          });
        } else {
          // $(".new_col").add("hide_variant");
        }

        }else{ }
       
     }
      }
    }
    
    variantOptionHide(evt){    

      let optionLabel = evt.target.value.replace(/\s+/g, '-').toLowerCase();
      var checked_flavor = $('.select-flavor .new_col').find('input[type="radio"]:checked').length;
      var checked_size = $('.cooki-size').find('input[type="radio"]:checked').length;
      if( document.querySelectorAll(".select-flavor .new_col").length > 0 ){
        if(checked_size == 0){
        var dataAttrValue = evt.target.getAttribute('data-size-type');
        if (dataAttrValue !== '' && dataAttrValue !== null && dataAttrValue !== undefined) {
            // Select the input element with the matching value
            $('.cooki-size input[data-val="'+dataAttrValue+'"]').trigger('click');
            
        }
      }
       
            
        let sizeType1 = window.theme.sizeOptionName.size_type_1.replace(/\s+/g, '-').toLowerCase();
        let sizeType2 = window.theme.sizeOptionName.size_type_2.replace(/\s+/g, '-').toLowerCase();
        let sizeType3 = window.theme.sizeOptionName.size_type_3.replace(/\s+/g, '-').toLowerCase();
        let pack_1 = window.metadata?.pack_1
        ? window.metadata.pack_1.replace(/\s+/g, '-').toLowerCase()
        : '';
        let pack_2 = window.metadata?.pack_2
        ? window.metadata.pack_2.replace(/\s+/g, '-').toLowerCase()
        : '';




        const variantShowHide_4z_note = document.querySelector(".note.note_oz-4-5-decadent-flavors");
        const variantShowHide_1oz_note = document.querySelector(".note.note_1-5-oz");
       
        if( optionLabel == sizeType2 ){
          const variantShowHide_4z = document.querySelectorAll(".select-flavor .new_col.oz-4-5-decadent-flavors");
          if(variantShowHide_4z_note){
            variantShowHide_4z_note.style.display = "block"
            variantShowHide_1oz_note.style.display = "none"
          }
          var i=1;          
          let optionSizes = window.theme.sizeOptionName.option4Oz;
          
          if( optionSizes !="" ){
            let optionSize = optionSizes.split(",");
            if( optionSize.length > 0 ){
              $(".select-flavor .new_col").addClass("hide_variant");
              $(".select-flavor .new_col").removeClass("show_variant");
              $('.variant-label').hide();
              $('.select-flavor .variant-label.4-oz').show();
              $('.giftbox-msg').hide();
              $('.select-flavor .giftbox-msg.4-oz').show().text('( 4.5 Oz Gift Pack Comes With 8 Cookies )');
              optionSize.forEach(function(opn, indx){
                variantShowHide_4z.forEach(function(item, inx){
                  if( item.classList.contains($.trim(opn)) ){
                    item.classList.remove("hide_variant");
                    item.classList.add("show_variant");
                    if( item.classList.contains("show_variant") && i == 1 ){
                      if( typeof evt !=="undefined"){
                        if( evt.target.closest(".select-size") != null && checked_flavor > 0){
                          $(item).find('input[type="radio"]').trigger("click");
                        }
                      }i++;
                    }
                  }else{
                    //item.classList.add("hide_variant");
                    //item.classList.remove("show_variant");
                  }
                });
              })
            }else{ //$(".new_col").add("hide_variant");
            }
          }
        }else if( optionLabel == sizeType3 ){
          const variantShowHide_3oz = document.querySelectorAll(".select-flavor .new_col.oz-153");
          if(variantShowHide_1oz_note){
            variantShowHide_4z_note.style.display = "none"
            variantShowHide_1oz_note.style.display = "block"
          }
          var j=1;
          let optionSizes = window.theme.sizeOptionName.option1Oz;
            
          if( optionSizes !="" ){
            let optionSize = optionSizes.split(",");
            if( optionSize.length > 0 ){
              $(".select-flavor .new_col").addClass("hide_variant");
              $(".select-flavor .new_col").removeClass("show_variant");
              $('.variant-label').hide();
              $('.select-flavor .variant-label.1_3-oz').show().text('3 oz Flavors');
              $('.giftbox-msg').hide();
              $('.select-flavor .giftbox-msg.1_3oz').show().text('( 3 Oz Gift Pack Comes With 12 Cookies )');
              optionSize.forEach(function(opn, indx){
                variantShowHide_3oz.forEach(function(item, inx){
                  if( item.classList.contains($.trim(opn)) ){
                    item.classList.remove("hide_variant");
                    item.classList.add("show_variant");
                    
                    if( item.classList.contains("show_variant") && j == 1 ){
                      if( typeof evt !=="undefined"){
                        if( evt.target.closest(".select-size") != null && checked_flavor > 0 ){
                          $(item).find('input[type="radio"]').trigger("click");
                        }
                      }j++;
                    }
                  }else{
                    //item.classList.add("hide_variant");
                    //item.classList.remove("show_variant");
                  }
                });
              })
            }else{ //$(".new_col").add("hide_variant");
            }
          }
        }else if( optionLabel == sizeType1 ){
        const variantShowHide = document.querySelectorAll(".select-flavor .new_col.oz-153");
          if(variantShowHide_1oz_note){
            variantShowHide_4z_note.style.display = "none"
            variantShowHide_1oz_note.style.display = "block"
          }
        var k = 1;
        let optionSizes1 = window.theme.sizeOptionName.option1Oz;
        let optionSizes2 = window.theme.sizeOptionName.gluten_free;
        
        // Merge both optionSizes arrays into one
        let combinedOptionSizes = optionSizes1.split(",").concat(optionSizes2.split(","));
        
        if (combinedOptionSizes.length > 0) {
          $(".select-flavor .new_col").addClass("hide_variant");
          $(".select-flavor .new_col").removeClass("show_variant");
          $('.variant-label').hide();
          $('.select-flavor .variant-label.gluten-free').show();
          $('.select-flavor .variant-label.1_3-oz').show().text('1.5 oz Flavors');
          $('.giftbox-msg').hide();
          $('.select-flavor .giftbox-msg.1_3oz').show().text('( 1.5 Oz Gift Pack Comes With 12 Cookies )');
          combinedOptionSizes.forEach(function(opn) {
            variantShowHide.forEach(function(item) {
              if (item.classList.contains($.trim(opn))) {
                item.classList.remove("hide_variant");
                item.classList.add("show_variant");
        
                if (item.classList.contains("show_variant") && k == 1) {
                  if (typeof evt !== "undefined") {
                    if (evt.target.closest(".select-size") != null && checked_flavor > 0) {
                      console.log('kkkkk');
                      $(item).find('input[type="radio"]').trigger("click");
                    }
                  }
                  k++;
                }
              } else {
                // item.classList.add("hide_variant");
                // item.classList.remove("show_variant");
              }
            });
          });
        } else {
          // $(".new_col").add("hide_variant");
        }

        }else if( optionLabel == pack_1 ){
          
            const variantShowHide = document.querySelectorAll(".select-flavor .new_col.oz-153");
            if(variantShowHide_1oz_note){
              variantShowHide_4z_note.style.display = "none"
              variantShowHide_1oz_note.style.display = "block"
            }
          var k = 1;
          let optionSizes1 = window.metadata.option_for_pack1;
       
        
        // Merge both optionSizes arrays into one
        let combinedOptionSizes = optionSizes1.split(",");

        if (combinedOptionSizes.length > 0) {
          $(".select-flavor .new_col").addClass("hide_variant");
          $(".select-flavor .new_col").removeClass("show_variant");
          // $('.variant-label').hide();
          // $('.select-flavor .variant-label.gluten-free').show();
          // $('.select-flavor .variant-label.1_3-oz').show().text('1.5 oz Flavors');
          combinedOptionSizes.forEach(function(opn) {
            variantShowHide.forEach(function(item) {
              if (item.classList.contains($.trim(opn))) {
                item.classList.remove("hide_variant");
                item.classList.add("show_variant");
        
                if (item.classList.contains("show_variant") && k == 1) {
                  if (typeof evt !== "undefined") {
                    if (evt.target.closest(".select-size") != null && checked_flavor > 0) {
                      console.log('kkkkk');
                      $(item).find('input[type="radio"]').trigger("click");
                    }
                  }
                  k++;
                }
              } else {
                // item.classList.add("hide_variant");
                // item.classList.remove("show_variant");
              }
            });
          });
          } else {
            // $(".new_col").add("hide_variant");
          }

        }else if( optionLabel == pack_2 ){
          
            const variantShowHide = document.querySelectorAll(".select-flavor .new_col.oz-153");
            if(variantShowHide_1oz_note){
              variantShowHide_4z_note.style.display = "none"
              variantShowHide_1oz_note.style.display = "block"
            }
          var k = 1;
          let optionSizes1 = window.metadata.option_for_pack2;
       
        
        // Merge both optionSizes arrays into one
        let combinedOptionSizes = optionSizes1.split(",");

        if (combinedOptionSizes.length > 0) {
          $(".select-flavor .new_col").addClass("hide_variant");
          $(".select-flavor .new_col").removeClass("show_variant");
          // $('.variant-label').hide();
          // $('.select-flavor .variant-label.gluten-free').show();
          // $('.select-flavor .variant-label.1_3-oz').show().text('1.5 oz Flavors');
          combinedOptionSizes.forEach(function(opn) {
            variantShowHide.forEach(function(item) {
              if (item.classList.contains($.trim(opn))) {
                item.classList.remove("hide_variant");
                item.classList.add("show_variant");
        
                if (item.classList.contains("show_variant") && k == 1) {
                  if (typeof evt !== "undefined") {
                    if (evt.target.closest(".select-size") != null && checked_flavor > 0) {
                      console.log('kkkkk');
                      $(item).find('input[type="radio"]').trigger("click");
                    }
                  }
                  k++;
                }
              } else {
                // item.classList.add("hide_variant");
                // item.classList.remove("show_variant");
              }
            });
          });
          } else {
            // $(".new_col").add("hide_variant");
          }

        }else{ }
       
     }

  }
    
    /**
     * Gets the variant data for a product.
     * @returns {?object}
     */
    getVariantData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get the selected values from a list of variant options.
     * @returns {Array} Array of selected option values, value is null if not selected.
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      return this.data.variants.find(
        (v) => v.options.every((val, index) => val === this.selectedOptions[index])
      );
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
